import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { checkUser } from "../auth";
import { config } from "../config";
import ApiClient from "../services/fuelandmoreapi";
import { UserContext, IUser } from "../contexts/UserContext";

interface AuthProviderProps {
  children: React.ReactNode;
}

interface DecodedToken {
  exp: number;
}

const isTokenExpired = (token: string | null): boolean => {
  console.log("Checking if token is expired:", token);
  if (!token) return true;
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    console.log("Token expiration time:", new Date(decoded.exp * 1000));
    return Date.now() >= decoded.exp * 1000;
  } catch {
    return true;
  }
};

const refreshTokens = async (refreshToken: string) => {
  console.log("Refreshing tokens with refresh token:", refreshToken);
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: config.clientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(`${config.cognitoTokenUrl}`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) {
    throw new Error("Token refresh failed");
  }

  return response.json();
};

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [user, setUser] = useState<IUser | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log("Initializing authentication...");
        let idToken = localStorage.getItem("id_token");
        let accessToken = localStorage.getItem("access_token");
        const refreshToken = localStorage.getItem("refresh_token");
        let storedUser = localStorage.getItem("user");

        if (idToken !== null && accessToken !== null) {
          console.log("Checking stored tokens...");

          // Refresh if access token is expired and we have a refresh token
          if (isTokenExpired(accessToken) && refreshToken) {
            try {
              console.log("Refreshing tokens...");
              const newTokens = await refreshTokens(refreshToken);
              idToken = newTokens.id_token;
              accessToken = newTokens.access_token;

              localStorage.setItem("id_token", idToken as string);
              localStorage.setItem("access_token", accessToken as string);
              if (newTokens.refresh_token) {
                localStorage.setItem("refresh_token", newTokens.refresh_token);
              }

              console.log("Tokens refreshed");
            } catch (err) {
              console.error("Token refresh failed", err);
              localStorage.clear();
              return redirectToLogin();
            }
          }

          console.log("Validating user from local storage...");
          if (storedUser && idToken && accessToken) {
            const userObj = JSON.parse(storedUser);
            if (userObj.email !== "undefined") {
              const apiClient = new ApiClient(config.apiBaseUrl);
              const validatedUser = await apiClient.getUserRole(userObj.email);
              const featureFlags = await apiClient.getFeatureFlags();

              console.log("User validation result:", validatedUser);

              if (validatedUser) {
                localStorage.setItem("user", JSON.stringify(validatedUser));
                localStorage.setItem(
                  "feature_flags",
                  JSON.stringify(featureFlags)
                );
                setUser(validatedUser);
                setIsAuthInitialized(true);
                return;
              }
            }
          }
        }

        // Fall back to Cognito login
        console.log("Falling back to Cognito login...");
        const cognitoUser = await checkUser();
        if (cognitoUser) {
          const apiClient = new ApiClient(config.apiBaseUrl);
          const validatedUser = await apiClient.getUserRole(cognitoUser.email);
          const featureFlags = await apiClient.getFeatureFlags();

          if (validatedUser) {
            localStorage.setItem("user", JSON.stringify(validatedUser));
            localStorage.setItem("feature_flags", JSON.stringify(featureFlags));
            setUser(validatedUser);
            setIsAuthInitialized(true);
            return;
          }
        }

        throw new Error("User authentication failed");
      } catch (error) {
        console.error("Authentication failed:", error);
        localStorage.clear();
        redirectToLogin();
      }
    };

    initializeAuth();
  }, [config.apiBaseUrl]);

  // Optional: periodic background refresh
  useEffect(() => {
    const interval = setInterval(
      async () => {
        const accessToken = localStorage.getItem("access_token");
        const refreshToken = localStorage.getItem("refresh_token");

        console.log("Checking tokens for periodic refresh...");
        if (isTokenExpired(accessToken) && refreshToken) {
          try {
            console.log("Refreshing tokens periodically...");
            const newTokens = await refreshTokens(refreshToken);
            localStorage.setItem("id_token", newTokens.id_token);
            localStorage.setItem("access_token", newTokens.access_token);
            if (newTokens.refresh_token) {
              console.log("Updating refresh token...");
              localStorage.setItem("refresh_token", newTokens.refresh_token);
            }
            console.log("Periodic token refresh successful");
          } catch (error) {
            console.error("Periodic token refresh failed", error);
            localStorage.clear();
            redirectToLogin();
          }
        }
      },
      5 * 60 * 1000
    ); // every 5 minutes

    return () => clearInterval(interval);
  }, []);

  const redirectToLogin = () => {
    const redirectUri = encodeURIComponent(window.location.origin);
    const loginUrl = `${config.cognitoLoginUrl}?client_id=${config.clientId}&response_type=code&scope=email+openid+phone+profile&redirect_uri=${redirectUri}`;
    window.location.href = loginUrl;
  };

  if (!isAuthInitialized || !user) {
    return <div>Loading...</div>;
  }

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};

export default AuthProvider;
